import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { DashboardLayout } from "@/components/DashboardLayout";
import { KCard } from "@/components/KCard";
import { Button } from "@/components/ui/button";
import {
  Tag,
  ArrowLeft,
  Loader2,
  BarChart2,
  Users,
  TrendingUp,
  Calendar,
  Target,
  Hash,
  Sparkles,
  CheckCircle2,
  XCircle,
  Clock,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { useAuth } from "@/auth/AuthContext";
import {
  getCoupon,
  getCouponUsageHistory,
  getCouponAnalytics,
  type Coupon,
  type CouponUsage,
  type CouponAnalytics,
} from "@/api/lms/coupons";

function getCouponStatus(coupon: Coupon): "active" | "expired" | "scheduled" | "disabled" {
  if (!coupon.isActive) return "disabled";
  const now = new Date();
  if (new Date(coupon.startDate) > now) return "scheduled";
  if (new Date(coupon.endDate) < now) return "expired";
  return "active";
}

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, { label: string; icon: React.ElementType; cls: string }> = {
    active: { label: "Active", icon: CheckCircle2, cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
    expired: { label: "Expired", icon: XCircle, cls: "bg-rose-50 text-rose-700 border-rose-200" },
    scheduled: { label: "Scheduled", icon: Clock, cls: "bg-blue-50 text-blue-700 border-blue-200" },
    disabled: { label: "Disabled", icon: AlertCircle, cls: "bg-gray-50 text-gray-600 border-gray-200" },
  };
  const s = map[status] ?? map.disabled;
  const Icon = s.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border ${s.cls}`}>
      <Icon size={12} />
      {s.label}
    </span>
  );
}

function InfoRow({ icon: Icon, label, value }: { icon: React.ElementType; label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-start gap-3 py-2.5 border-b border-border last:border-0">
      <div className="w-7 h-7 rounded-lg bg-muted flex items-center justify-center shrink-0 mt-0.5">
        <Icon size={14} className="text-muted-foreground" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide">{label}</p>
        <div className="text-sm font-medium text-foreground mt-0.5">{value}</div>
      </div>
    </div>
  );
}

function userName(u?: CouponUsage["user"]): string {
  if (!u) return "—";
  const fn = u.profile?.firstName ?? "";
  const ln = u.profile?.lastName ?? "";
  const full = `${fn} ${ln}`.trim();
  return full || u.email;
}

export default function CouponDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const isAdmin = user?.backendRole === "SYSTEM_ADMIN";
  const basePath = isAdmin ? "/admin" : "/teacher";

  const [coupon, setCoupon] = useState<Coupon | null>(null);
  const [analytics, setAnalytics] = useState<CouponAnalytics | null>(null);
  const [usages, setUsages] = useState<CouponUsage[]>([]);
  const [usageTotal, setUsageTotal] = useState(0);
  const [usagePage, setUsagePage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [usageLoading, setUsageLoading] = useState(false);

  const pageSize = 20;

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    Promise.all([getCoupon(id), getCouponAnalytics(id)])
      .then(([c, a]) => {
        setCoupon(c);
        setAnalytics(a);
      })
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    if (!id) return;
    setUsageLoading(true);
    getCouponUsageHistory(id, { page: usagePage, limit: pageSize })
      .then((res) => {
        setUsages(res.items);
        setUsageTotal(res.total);
      })
      .finally(() => setUsageLoading(false));
  }, [id, usagePage]);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="dash-page flex items-center justify-center">
          <div className="dash-empty-state">
            <Loader2 className="animate-spin mb-2 size-5" />
            <p className="text-sm font-medium">Loading coupon details...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (!coupon) {
    return (
      <DashboardLayout>
        <div className="dash-page flex items-center justify-center">
          <div className="dash-empty-state">
            <p className="text-sm font-medium mb-4">Coupon not found.</p>
            <Button asChild variant="outline" size="sm">
              <Link to={`${basePath}/coupons`}>
                <ArrowLeft size={13} className="mr-1" /> Back to Coupons
              </Link>
            </Button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  const status = getCouponStatus(coupon);
  const usedPct = coupon.usageLimit
    ? Math.min(100, Math.round((coupon.totalUsed / coupon.usageLimit) * 100))
    : 0;
  const totalPages = Math.ceil(usageTotal / pageSize);

  return (
    <DashboardLayout>
      <div className="dash-page">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <Button asChild variant="ghost" size="sm" className="text-muted-foreground">
            <Link to={`${basePath}/coupons`}>
              <ArrowLeft size={14} className="mr-1" /> Coupons
            </Link>
          </Button>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 mb-8">
          <div className="flex items-start gap-3">
            <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center shrink-0">
              {coupon.isAutoPromotion ? (
                <Sparkles size={22} className="text-amber-500" />
              ) : (
                <Tag size={22} className="text-primary" />
              )}
            </div>
            <div>
              <h1 className="text-xl font-bold text-foreground">{coupon.name}</h1>
              {coupon.code && (
                <p className="text-sm font-mono text-muted-foreground bg-muted px-2 py-0.5 rounded mt-1 inline-block">
                  {coupon.code}
                </p>
              )}
              {coupon.isAutoPromotion && (
                <p className="text-xs text-amber-600 font-semibold flex items-center gap-1 mt-1">
                  <Sparkles size={11} /> Auto-promotion
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <StatusBadge status={status} />
            <Button asChild size="sm" variant="outline">
              <Link to={`${basePath}/coupons?edit=${coupon.id}`}>
                Edit Coupon
              </Link>
            </Button>
          </div>
        </div>

        {/* Analytics + Info grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {/* Coupon Info */}
          <KCard className="lg:col-span-1 p-5">
            <h2 className="text-sm font-bold text-foreground mb-3">Coupon Details</h2>
            <div className="divide-y divide-border">
              <InfoRow
                icon={Tag}
                label="Discount"
                value={
                  <span className="text-primary font-bold text-base">
                    {coupon.discountType === "PERCENTAGE"
                      ? `${parseFloat(coupon.discountValue)}% off`
                      : `৳${parseFloat(coupon.discountValue).toLocaleString()} off`}
                  </span>
                }
              />
              <InfoRow
                icon={Target}
                label="Target"
                value={
                  <span className="capitalize">
                    {coupon.targetType.replace(/_/g, " ").toLowerCase()}
                    {coupon.targetId && (
                      <span className="block text-[11px] text-muted-foreground font-mono mt-0.5">
                        ID: {coupon.targetId}
                      </span>
                    )}
                  </span>
                }
              />
              <InfoRow
                icon={Calendar}
                label="Validity"
                value={
                  <div>
                    <span className="block">{new Date(coupon.startDate).toLocaleDateString("en-BD", { dateStyle: "medium" })}</span>
                    <span className="block text-muted-foreground">→ {new Date(coupon.endDate).toLocaleDateString("en-BD", { dateStyle: "medium" })}</span>
                  </div>
                }
              />
              <InfoRow
                icon={Hash}
                label="Usage Limit"
                value={coupon.usageLimit !== null ? `${coupon.totalUsed} / ${coupon.usageLimit}` : "Unlimited"}
              />
              {coupon.usageLimit && (
                <div className="py-2">
                  <div className="flex justify-between text-[11px] text-muted-foreground mb-1">
                    <span>Used</span>
                    <span>{usedPct}%</span>
                  </div>
                  <div className="w-full bg-muted rounded-full h-1.5">
                    <div
                      className={`h-1.5 rounded-full transition-all ${usedPct >= 90 ? "bg-rose-500" : "bg-primary"}`}
                      style={{ width: `${usedPct}%` }}
                    />
                  </div>
                </div>
              )}
              <InfoRow
                icon={Users}
                label="Per User Limit"
                value={`${coupon.perUserLimit} use${coupon.perUserLimit !== 1 ? "s" : ""} per student`}
              />
              <InfoRow
                icon={Calendar}
                label="Created"
                value={new Date(coupon.createdAt).toLocaleDateString("en-BD", { dateStyle: "medium" })}
              />
            </div>
          </KCard>

          {/* Analytics */}
          <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-3 gap-4 content-start">
            {[
              {
                label: "Total Uses",
                value: analytics?.totalUses ?? 0,
                sub: "Redemptions",
                icon: Users,
                color: "bg-blue-50 text-blue-600",
              },
              {
                label: "Total Savings",
                value: `৳${(analytics?.totalSavings ?? 0).toLocaleString()}`,
                sub: "Discounts given",
                icon: TrendingUp,
                color: "bg-emerald-50 text-emerald-600",
              },
              {
                label: "Revenue Generated",
                value: `৳${(analytics?.totalRevenue ?? 0).toLocaleString()}`,
                sub: "After discounts",
                icon: BarChart2,
                color: "bg-violet-50 text-violet-600",
              },
            ].map((s, i) => (
              <KCard key={i} className="dash-stat-card">
                <div className={`dash-stat-icon ${s.color}`}>
                  <s.icon size={18} />
                </div>
                <div>
                  <p className="dash-stat-value text-xl">{s.value}</p>
                  <p className="dash-stat-label mt-1">{s.label}</p>
                </div>
                <p className="text-[11px] text-muted-foreground/70">{s.sub}</p>
              </KCard>
            ))}

            {/* Description */}
            {coupon.description && (
              <KCard className="sm:col-span-3 p-4">
                <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide mb-1">Description</p>
                <p className="text-sm text-foreground">{coupon.description}</p>
              </KCard>
            )}
          </div>
        </div>

        {/* Usage History */}
        <div>
          <div className="dash-section-header mb-4">
            <h2 className="dash-section-title">Usage History</h2>
            <span className="text-xs text-muted-foreground">{usageTotal} total uses</span>
          </div>
          <KCard className="overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full dash-table">
                <thead>
                  <tr>
                    <th>Student</th>
                    <th>Product</th>
                    <th>Original</th>
                    <th>Discount</th>
                    <th>Final</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {usageLoading ? (
                    <tr>
                      <td colSpan={6} className="p-4">
                        <div className="dash-empty-state py-6 border-none shadow-none bg-transparent">
                          <Loader2 className="animate-spin mb-2 size-5" />
                          <p className="text-sm font-medium">Loading...</p>
                        </div>
                      </td>
                    </tr>
                  ) : usages.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="p-4">
                        <div className="dash-empty-state py-6 border-none shadow-none bg-transparent">
                          <p className="text-sm font-medium">No redemptions yet.</p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    usages.map((u) => (
                      <tr key={u.id}>
                        <td>
                          <p className="text-sm font-medium text-foreground">{userName(u.user)}</p>
                          <p className="text-[11px] text-muted-foreground">{u.user?.email}</p>
                        </td>
                        <td>
                          <span className={`dash-badge text-[10px] ${u.productType === "COURSE" ? "bg-violet-50 text-violet-700" : "bg-amber-50 text-amber-700"}`}>
                            {u.productType}
                          </span>
                          {u.productName && (
                            <p className="text-[11px] text-muted-foreground mt-0.5 truncate max-w-[120px]">
                              {u.productName}
                            </p>
                          )}
                        </td>
                        <td className="font-mono text-xs text-muted-foreground">
                          ৳{parseFloat(u.originalPrice).toLocaleString()}
                        </td>
                        <td className="font-mono text-xs text-emerald-600 font-semibold">
                          −৳{parseFloat(u.discountAmount).toLocaleString()}
                        </td>
                        <td className="font-mono text-xs font-bold text-foreground">
                          ৳{parseFloat(u.finalPrice).toLocaleString()}
                        </td>
                        <td className="text-[11px] text-muted-foreground">
                          {new Date(u.usedAt).toLocaleDateString("en-BD", { dateStyle: "medium" })}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-border">
                <p className="text-xs text-muted-foreground">
                  {(usagePage - 1) * pageSize + 1}–{Math.min(usagePage * pageSize, usageTotal)} of {usageTotal}
                </p>
                <div className="flex gap-1">
                  <Button size="sm" variant="outline" disabled={usagePage === 1} onClick={() => setUsagePage((p) => p - 1)}>
                    <ChevronLeft size={13} />
                  </Button>
                  <Button size="sm" variant="outline" disabled={usagePage === totalPages} onClick={() => setUsagePage((p) => p + 1)}>
                    <ChevronRight size={13} />
                  </Button>
                </div>
              </div>
            )}
          </KCard>
        </div>
      </div>
    </DashboardLayout>
  );
}
